import {
   BehaviorSubject,
   catchError,
   filter,
   firstValueFrom,
   forkJoin,
   Observable,
   of,
   Subscription,
   take,
   timer,
} from 'rxjs';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { Router } from '@angular/router';
import { environment } from '@env';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import {
   AccessType,
   AuthUser,
   CompanyToken,
   CompanyTokenItem,
   PermissionType,
} from 'src/app/models/auth.model';
import { AuthorizationToken } from 'src/app/models/authorization-token.model';
import { buildApiUrl } from '../api/api-url.util';
import { FunctionService } from '../utils/function.service';
import { ApplicationInsightsService } from './application-insights.service';
import { MixpanelService } from './mixpanel.service';
import { SessionStorageService } from './session-storage.service';

@Injectable({
   providedIn: 'root',
})
export class AuthService {
   private router = inject(Router);
   private http = inject(HttpClient);
   private functionService = inject(FunctionService);
   private localStorageService = inject(LocalStorageService);
   private sessionStorageService = inject(SessionStorageService);
   private auth0Service = inject(Auth0Service);
   private translateService = inject(TranslateService);
   private applicationInsightsService = inject(ApplicationInsightsService);

   get nativeWindow(): any {
      return window;
   }

   private refreshSubscription?: Subscription;
   private changedCompany: BehaviorSubject<boolean>;
   private authorizationTokenCreated: BehaviorSubject<boolean>;
   private mixpanelService: MixpanelService;
   private mfaReauthenticationStarted = false;
   private lastRoles: string[];

   /** Inserted by Angular inject() migration for backwards compatibility */
   constructor(...args: unknown[]);

   constructor() {
      this.changedCompany = new BehaviorSubject(false);
      this.authorizationTokenCreated = new BehaviorSubject(
         this.localStorageService.getAuthorizationToken() !== null,
      );
      this.setAppInsightsUser();
   }

   private setAppInsightsUser() {
      const user = this.getUserInfo();
      if (user && user.Email) {
         this.applicationInsightsService.setUserContext(user.Email);
      }
   }

   get authorizationTokenObservable() {
      return this.authorizationTokenCreated.asObservable();
   }

   setAuthorizationTokenCreated(val: any) {
      this.authorizationTokenCreated.next(val);
   }

   isParentCompany(companyId: number): boolean {
      const companyToken = this.getCompany(companyId);
      return companyToken.ParentId == companyToken.CompanyId;
   }

   startUniversalLogin(
      target: string = '/dashboard',
      loginHint?: string,
      invitationId?: string,
      goToCompletePage = false,
   ): Promise<void> {
      sessionStorage.setItem(
         'erp-auth-context',
         JSON.stringify({ invitationId, goToCompletePage }),
      );
      const language = this.translateService.currentLang || this.translateService.getFallbackLang() || 'pt-BR';
      return firstValueFrom(
         this.auth0Service.loginWithRedirect({
            appState: { target },
            authorizationParams: {
               ui_locales: language,
               ...(loginHint ? { login_hint: loginHint } : {}),
            },
         }),
      );
   }

   cancelUniversalLogin(): void {
      sessionStorage.removeItem('erp-auth-context');
   }

   async initializeUniversalLogin(): Promise<void> {
      const isAuthenticated = await firstValueFrom(
         this.auth0Service.isAuthenticated$.pipe(take(1)),
      );
      if (!isAuthenticated) {
         if (this.localStorageService.getAccessToken()) {
            this.removeStorage();
         }
         return;
      }

      try {
         const { accessToken, idTokenClaims } = await firstValueFrom(
            forkJoin({
               accessToken: this.auth0Service.getAccessTokenSilently(),
               idTokenClaims: this.auth0Service.idTokenClaims$.pipe(
                  filter((claims) => !!claims?.__raw),
                  take(1),
               ),
            }),
         );
         if (
            accessToken === this.localStorageService.getAccessToken() &&
            this.localStorageService.getAuthorizationToken()
         ) {
            return;
         }

         const decodedAccessToken = this.functionService.decodeToken(accessToken);
         const expiresIn = Math.max(
            1,
            Number(decodedAccessToken?.exp || 0) - Math.floor(Date.now() / 1000),
         );
         const loginContext = this.consumeLoginContext();
         const completed = await this.setLocalStorage(
            {
               accessToken,
               idToken: idTokenClaims!.__raw,
               expiresIn,
            },
            false,
            loginContext.invitationId,
            loginContext.goToCompletePage,
         );
         if (!completed && !this.mfaReauthenticationStarted) {
            this.logoutTime();
         }
      } catch {
         this.logoutTime();
      }
   }

   private consumeLoginContext(): { invitationId?: string; goToCompletePage: boolean } {
      const raw = sessionStorage.getItem('erp-auth-context');
      sessionStorage.removeItem('erp-auth-context');
      if (!raw) return { goToCompletePage: false };

      try {
         return JSON.parse(raw);
      } catch {
         return { goToCompletePage: false };
      }
   }

   async requestPasswordChange(): Promise<void> {
      const email = this.getUserInfo()?.Email;
      if (!email) return Promise.reject(new Error('Authenticated user email is unavailable.'));

      await firstValueFrom(
         this.http.post(
            `https://${environment.auth0Config.domain}/dbconnections/change_password`,
            {
               client_id: environment.auth0Config.clientID,
               connection: 'Username-Password-Authentication',
               email,
            },
            { responseType: 'text' },
         ),
      );
   }

   private async setLocalStorage(
      authResult: any,
      isSilentAuthentication: boolean,
      invitationId?: string,
      goToCompletePage = false,
   ): Promise<boolean> {
      // Setting the time that the access token will expire at
      const expiresAt = JSON.stringify(authResult.expiresIn * 1000 + new Date().getTime());

      // Setting login info on localStorage
      this.localStorageService.setAccessToken(authResult.accessToken);
      this.localStorageService.setIdToken(authResult.idToken);
      this.localStorageService.setExpiresAt(expiresAt);

      this.setAppInsightsUser();

      this.updateUserLastLogin(authResult.accessToken).subscribe((ret) => {
         this.scheduleRenewal();
      });

      if (!isSilentAuthentication) {
         //---localStorage
         this.localStorageService.removeCompanyToken();
         this.localStorageService.removeCompanyIdsToken();
         this.localStorageService.removeCompanySetup();
         this.localStorageService.removeCompanyPlan();

         //---sessionStorage
         this.sessionStorageService.removeSearchCustomFilters();
         this.sessionStorageService.removeSearchPageIndex();
         this.sessionStorageService.removeSearchPageLength();
         this.sessionStorageService.removeSearchAll();

         return this.createAuthorizationToken(true, invitationId, goToCompletePage);
      }

      return true;
   }

   renewAuthentication(): void {
      this.auth0Service
         .getAccessTokenSilently({ detailedResponse: true })
         .pipe(take(1))
         .subscribe({
            next: (result) => {
               const decodedAccessToken = this.functionService.decodeToken(result.access_token);
               const expiresIn = Math.max(
                  1,
                  Number(decodedAccessToken?.exp || 0) - Math.floor(Date.now() / 1000),
               );
               void this.setLocalStorage(
                  {
                     accessToken: result.access_token,
                     idToken: this.localStorageService.getIdToken(),
                     expiresIn,
                  },
                  true,
               );
            },
            error: () => this.requireMfaReauthentication(),
         });
   }

   updateUserLastLogin(accessToken: any): Observable<boolean> {
      const url = buildApiUrl(environment.apiBaseUrl, 'UserLogin/LastLogin');

      const headers = new HttpHeaders({
         Authorization: 'Bearer ' + accessToken,
         'Content-Type': 'application/json',
      });

      return this.http
         .put<any>(url, null, { headers: headers })
         .pipe(catchError((error) => of('error', error.json())));
   }

   scheduleRenewal() {
      this.unscheduleRenewal();

      const currentTimestamp = new Date().getTime();
      const expiresAt = parseInt(this.localStorageService.getExpiresAt()!, 10);
      if (expiresAt && !isNaN(expiresAt) && expiresAt > currentTimestamp) {
         const delay = expiresAt - currentTimestamp - 600000;
         this.refreshSubscription = timer(delay > 0 ? delay : 0).subscribe(() => {
            this.renewAuthentication();
         });
      }
   }

   unscheduleRenewal() {
      this.refreshSubscription?.unsubscribe();
      this.refreshSubscription = undefined;
   }

   getCompanies(): CompanyToken {
      const encodedCompanies = this.localStorageService.getCompanyToken();
      if (encodedCompanies) {
         return JSON.parse(this.functionService.decodeString(encodedCompanies));
      }
      return null!;
   }

   getAuthorizationToken(companyToken: CompanyToken, invitationToken?: string): Observable<string> {
      const url = buildApiUrl(environment.apiBaseUrl, 'AuthorizationToken');
      const companyIds: number[] = [];

      if (companyToken && companyToken.CompanyItems) {
         companyToken.CompanyItems.forEach((item) => {
            companyIds.push(item.CompanyId);
         });
      }

      const headers = new HttpHeaders({
         Authorization: 'Bearer ' + this.localStorageService.getAccessToken(),
         'Content-Type': 'application/json',
      });

      const body: { CompanyIds: number[]; InvitationId?: string } = {
         CompanyIds: companyIds,
      };

      if (invitationToken) {
         body.InvitationId = invitationToken;
      }

      return this.http.post(url, body, { headers: headers, responseType: 'text' });
   }

   public async createAuthorizationToken(
      redirectToHome: boolean,
      invitationId?: string,
      goToCompletePage = false,
   ): Promise<boolean> {
      this.setAuthorizationTokenCreated(false);
      try {
         let companies = this.getCompanies();
         const token = await firstValueFrom(this.getAuthorizationToken(companies, invitationId));
         this.localStorageService.setAuthorizationToken(token);

         try {
            const authToken = JSON.parse(this.functionService.decodeString(token));
            if (authToken?.CompaniesWithoutConfig) {
               this.localStorageService.setCompanySetup(authToken.CompaniesWithoutConfig);
            }
            if (authToken?.Plan) {
               this.localStorageService.setCompanyPlan(authToken.Plan);
            }
         } catch {
            // Some environments can return token in a different payload format.
            // Keep login flow alive even when optional auth-token metadata is unavailable.
         }
         this.setAppInsightsUser();

         if (!companies || companies.CompanyItems.length === 0) {
            const userCompanies = await firstValueFrom(this.getUserCompanies());
            if (!Array.isArray(userCompanies) || userCompanies.length === 0) {
               throw new Error('Unable to load user companies.');
            }

            companies = {
               ShowCompanySelection: userCompanies.length > 1,
               CompanyItems: [],
            };

            userCompanies.forEach((item: any) => {
               companies.CompanyItems.push(item);
            });

            await this.setCompanies(companies, false);
         }

         this.redirectAfterLogin(goToCompletePage, redirectToHome);

         // if (goToCompletePage) {
         //    this.router.navigate(['/signup/success']);
         // } else if (!this.isAuthorized(this.lastRoles) || redirectToHome) {
         //    this.router.navigate(['/dashboard']);
         // }

         this.setAuthorizationTokenCreated(true);
         return true;
      } catch (error) {
         this.setAuthorizationTokenCreated(false);
         if (this.isMfaRequiredError(error)) {
            this.requireMfaReauthentication();
         }
         return false;
      }
   }

   private isMfaRequiredError(error: unknown): boolean {
      const response = error as { status?: number; error?: { Code?: string; code?: string } };
      return (
         response?.status === 403 &&
         (response.error?.Code === 'mfa_required' || response.error?.code === 'mfa_required')
      );
   }

   requireMfaReauthentication(): void {
      if (this.mfaReauthenticationStarted) return;

      this.mfaReauthenticationStarted = true;
      const email = this.getUserInfo()?.Email;
      this.removeStorage();
      void firstValueFrom(
         this.auth0Service.loginWithRedirect({
            appState: { target: '/dashboard' },
            authorizationParams: {
               prompt: 'login',
               max_age: 0,
               ...(email ? { login_hint: email } : {}),
            },
         }),
      ).catch(() => {
         this.mfaReauthenticationStarted = false;
         this.router.navigate(['/login']);
      });
   }

   redirectAfterLogin(goToCompletePage: boolean, redirectToHome: boolean): void {
      if (goToCompletePage) {
         this.router.navigate(['/signup/success']);
      } else if (redirectToHome) {
         this.router.navigate(['/dashboard']);
      }
   }

   login(): void {
      this.router.navigate(['/login']);
   }

   loginTime(): void {
      this.router.navigate(['/login']);
   }

   logout(): void {
      this.removeStorage();

      this.unscheduleRenewal();
      void firstValueFrom(
         this.auth0Service.logout({
            logoutParams: { returnTo: environment.auth0Config.redirectUri },
         }),
      );
   }

   logoutTime(): void {
      this.removeStorage();

      this.unscheduleRenewal();
      void firstValueFrom(
         this.auth0Service.logout({
            logoutParams: { returnTo: environment.auth0Config.redirectUri },
         }),
      );
   }

   removeStorage() {
      this.localStorageService.removeAccessToken();
      this.localStorageService.removeAuthorizationToken();
      this.localStorageService.removeCompanyToken();
      this.localStorageService.removeCompanyIdsToken();
      this.localStorageService.removeCompanySetup();
      this.localStorageService.removeCompanyPlan();
      this.localStorageService.removeExpiresAt();
      this.localStorageService.removeIdToken();
      this.localStorageService.removeCompanyId();
      this.localStorageService.removeFilterOData();
      this.applicationInsightsService.clearUserContext();
   }

   getCompaniesNeedsSetup(): number[] {
      const companies: number[] = [];
      const setupToken = this.localStorageService.getCompanySetup();
      if (setupToken) {
         const spltitedCompanies = setupToken.split(',');
         spltitedCompanies.forEach((company: any) => {
            companies.push(parseInt(company, 10));
         });
      }
      return companies;
   }

   setCompanySetupCompleted(companyId: number) {
      const setupToken = this.localStorageService.getCompanySetup();
      if (setupToken) {
         let newSetupToken = '';
         const spltitedCompanies = setupToken.split(',');
         spltitedCompanies.forEach((company: any) => {
            const currCompanyId = parseInt(company, 10);
            if (currCompanyId !== companyId) {
               newSetupToken += ',' + currCompanyId;
            }
         });
         if (newSetupToken.length > 0) {
            newSetupToken = newSetupToken.substring(1);
         }
         this.localStorageService.setCompanySetup(newSetupToken);
      }
   }

   isAuthenticated(queryParams?: any, redirectToLogin: boolean = true): boolean {
      const accessToken = this.localStorageService.getAccessToken();
      const expiresAt = parseInt(this.localStorageService.getExpiresAt()!, 10);

      if (accessToken && expiresAt > new Date().getTime()) {
         const user = this.getUserInfo();

         //---mixpanel
         this.mixpanelService?.identify(user.Email);
         this.mixpanelService?.setPeople({
            $email: user.Email,
            name: user.Name,
         });
         this.mixpanelService?.track('Authentication', { Email: user.Email });

         return true;
      }

      if (redirectToLogin) {
         this.logoutTime();
      }

      return false;
   }

   isAuthorized(roles: string[]): boolean {
      this.lastRoles = roles;

      if (roles && roles.length > 0) {
         let hasValidRole = false;
         const permissions = this.getPermissions();

         // Checking if the user has any of the given permissions
         roles.forEach((role) => {
            if (permissions.includes(role)) {
               hasValidRole = true;
            }
         });
         return hasValidRole;
      }
      return true;
   }

   getUserInfo(): AuthUser {
      const idToken = this.functionService.decodeToken(this.localStorageService.getIdToken());

      let user: AuthUser;

      if (idToken) {
         user = new AuthUser(
            idToken.name,
            idToken.nickname,
            idToken.picture,
            idToken.email,
            idToken.email_verified,
         );
      }
      return user!;
   }

   isKcmsUser(): boolean {
      const idToken = this.functionService.decodeToken(this.localStorageService.getIdToken()!);
      if (idToken && idToken.email && idToken.email.includes('@kcms.com.br')) {
         return true;
      }
      return false;
   }

   isUserCompanyAdmin(): boolean {
      const authToken = this.localStorageService.getAuthorizationToken();

      if (authToken) {
         const decodedAuthToken: AuthorizationToken = JSON.parse(
            this.functionService.decodeString(authToken),
         );

         if (decodedAuthToken) {
            return decodedAuthToken.IsCompanyAdmin;
         }
      }

      return false;
   }

   getPermissions(): string[] {
      const authToken = this.localStorageService.getAuthorizationToken();

      if (authToken) {
         const decodedAuthToken: AuthorizationToken = JSON.parse(
            this.functionService.decodeString(authToken),
         );

         if (decodedAuthToken) {
            return decodedAuthToken.Permissions.split(',');
         }
      }

      return [];
   }

   getCompaniesFromGroups(domain: string, accessType?: string): number[] {
      const authToken = this.localStorageService.getAuthorizationToken();
      const companyIds: number[] = [];

      if (authToken) {
         const decodedAuthToken: AuthorizationToken = JSON.parse(
            this.functionService.decodeString(authToken),
         );

         if (decodedAuthToken) {
            const groups = decodedAuthToken.Groups.split(',');

            // Checking if the given domain/access type exists on the user groups. If so, gets its CompanyId
            groups.forEach((group) => {
               const splittedGroup = group.split('-');

               if (
                  splittedGroup.length === 3 &&
                  splittedGroup[1] === domain &&
                  (!accessType || (accessType && splittedGroup[2] === accessType))
               ) {
                  const companyId = parseInt(splittedGroup[0], 10);

                  if (!companyIds.includes(companyId)) {
                     companyIds.push(companyId);
                  }
               }
            });
         }
      }

      return companyIds;
   }

   async setCompanies(
      companies: CompanyToken,
      recreateAuthToken: boolean = true,
   ): Promise<boolean> {
      if (companies) {
         const encodedCompanies = this.functionService.encodeString(JSON.stringify(companies));
         this.localStorageService.setCompanyToken(encodedCompanies);

         let companyIds = '';
         const kcmsCompany = companies.CompanyItems.find((n) => n.CompanyId === 1);

         if (kcmsCompany) {
            companyIds = kcmsCompany.ParentId.toString() + '-' + kcmsCompany.CompanyId.toString();
         } else {
            for (let i = 0; i < companies.CompanyItems.length; i++) {
               if (i > 0) {
                  companyIds += ',';
               }
               companyIds +=
                  companies.CompanyItems[i].ParentId.toString() +
                  '-' +
                  companies.CompanyItems[i].CompanyId.toString();
            }
         }

         this.localStorageService.setCompanyIdsToken(companyIds);
      } else {
         this.localStorageService.removeCompanyToken();
         this.localStorageService.removeCompanyIdsToken();
      }

      if (recreateAuthToken) {
         return await this.createAuthorizationToken(false);
      }

      return true;
   }

   getCompany(companyId: number): CompanyTokenItem {
      const encodedCompanies = this.localStorageService.getCompanyToken();
      if (encodedCompanies) {
         const companies: CompanyToken = JSON.parse(
            this.functionService.decodeString(encodedCompanies),
         );
         if (companies !== null && companies.CompanyItems !== null) {
            return companies.CompanyItems.find((n) => n.CompanyId === Number(companyId))!;
         }
      }
      return null!;
   }

   hasReadPermission(domain: string, companyId?: number): boolean {
      return this.hasPermission(domain, AccessType.Read, companyId);
   }

   hasCreatePermission(domain: string, companyId?: number): boolean {
      return this.hasPermission(domain, AccessType.Create, companyId);
   }

   hasUpdatePermission(domain: string, companyId?: number): boolean {
      return this.hasPermission(domain, AccessType.Update, companyId);
   }

   hasPermission(domain: string, accessType: AccessType, companyId?: number): boolean {
      if (companyId) {
         const authToken = this.localStorageService.getAuthorizationToken();
         if (authToken) {
            const decodedAuthToken: AuthorizationToken = JSON.parse(
               this.functionService.decodeString(authToken),
            );

            const splittedGroups = decodedAuthToken.Groups.split(',');
            const companies = this.getCompanies();

            for (let i = 0; i < splittedGroups.length; i++) {
               if (splittedGroups[i] === companyId.toString() + '-' + domain + '-' + accessType) {
                  return true;
               } else if (splittedGroups[i] === '1-' + domain + '-' + accessType) {
                  for (let n = 0; n < companies.CompanyItems.length; n++) {
                     if (
                        companies.CompanyItems[n].CompanyId.toString() === companyId.toString() ||
                        companies.CompanyItems[n].CompanyId === 1
                     ) {
                        return true;
                     }
                  }
               }
            }
         }
      } else {
         const permissions = this.getPermissions();
         return permissions.includes(domain + '-' + accessType);
      }

      return false;
   }

   getPermissionType(domain: string, accessType: AccessType, companyId: number): PermissionType {
      let hasChildPermission = false;
      const authToken = this.localStorageService.getAuthorizationToken();

      if (authToken) {
         const decodedAuthToken: AuthorizationToken = JSON.parse(
            this.functionService.decodeString(authToken),
         );

         let hasCompanySelected = false;
         const childrenCompanies: number[] = [];
         const companies = this.getCompanies();
         companies.CompanyItems.forEach((company) => {
            if (company.CompanyId === companyId) {
               hasCompanySelected = true;
            }
            if (company.ParentId === companyId) {
               childrenCompanies.push(company.CompanyId);
            }
         });
         const splittedGroups = decodedAuthToken.Groups.split(',');
         for (let i = 0; i < splittedGroups.length; i++) {
            const splittedGroup = splittedGroups[i].split('-');
            const groupCompanyId = parseInt(splittedGroup[0], 10);
            const groupPermission = splittedGroup[1] + '-' + splittedGroup[2];
            if (groupPermission === domain + '-' + accessType) {
               if (groupCompanyId === companyId) {
                  return PermissionType.Owner;
               } else if (groupCompanyId === 1) {
                  if (
                     hasCompanySelected ||
                     childrenCompanies.includes(companyId) ||
                     childrenCompanies.includes(1)
                  ) {
                     return PermissionType.Owner;
                  } else {
                     for (let x = 0; x < companies.CompanyItems.length; x++) {
                        if (
                           companies.CompanyItems[x].ParentId === companyId &&
                           childrenCompanies.includes(companies.CompanyItems[x].CompanyId)
                        ) {
                           hasChildPermission = true;
                           break;
                        }
                     }
                  }
               } else if (childrenCompanies.includes(groupCompanyId)) {
                  hasChildPermission = true;
               }
            }
         }
      }
      return hasChildPermission ? PermissionType.Child : PermissionType.None;
   }

   getCompanyIdsByPermission(
      domain: string,
      accessType: AccessType,
      onlyParentCompanies = false,
   ): number[] {
      const authToken = this.localStorageService.getAuthorizationToken();
      const companies: number[] = [];

      if (authToken) {
         const decodedAuthToken: AuthorizationToken = JSON.parse(
            this.functionService.decodeString(authToken),
         );
         const splittedGroups = decodedAuthToken.Groups.split(',');
         for (let i = 0; i < splittedGroups.length; i++) {
            const splittedGroup = splittedGroups[i].split('-');
            const groupCompanyId = parseInt(splittedGroup[0], 10);
            const groupPermission = splittedGroup[1] + '-' + splittedGroup[2];
            if (
               groupPermission === domain + '-' + accessType &&
               !companies.includes(groupCompanyId)
            ) {
               companies.push(groupCompanyId);
            }
         }

         if (onlyParentCompanies) {
            const encodedCompanies = this.localStorageService.getCompanyToken();
            const companyToken: CompanyToken = JSON.parse(
               this.functionService.decodeString(encodedCompanies!),
            );
            for (let i = 0; i < companyToken.CompanyItems.length; i++) {
               const index = companies.indexOf(companyToken.CompanyItems[i].CompanyId);
               if (
                  index >= 0 &&
                  companyToken.CompanyItems[i].ParentId !== companyToken.CompanyItems[i].CompanyId
               ) {
                  companies.splice(index, 1);
               }
            }
         }
      }

      return companies;
   }

   getUserCompanies(): Observable<Array<CompanyTokenItem>> {
      const url = buildApiUrl(environment.apiBaseUrl, 'UserCompany');

      const headers = new HttpHeaders({
         Authorization: 'Bearer ' + this.localStorageService.getAccessToken(),
         'Company-Token': this.localStorageService.getCompanyIdsToken()
            ? this.localStorageService.getCompanyIdsToken()
            : '',
         'Content-Type': 'application/json',
      });

      return this.http
         .get<any>(url, { headers: headers })
         .pipe(catchError((error) => of(error.json())));
   }
}
